/**
 * India-first phone + optional-email helpers for the kundali / chat-entry form.
 *
 * Pure, dependency-free, fully unit-tested so the web coverage ratchet
 * (~93% funcs / 81% branches) stays green — a new lib file with untested
 * functions tips the GLOBAL gate red and blocks all deploys.
 *
 * The seeker types a 10-digit Indian mobile; we render a fixed `+91` prefix in
 * the UI, so the input itself only holds the national number. `normalizeIndianPhone`
 * accepts the lenient real-world shapes anyway (a pasted `+91 98765 43210`,
 * `091…`, a leading `0`, spaces/dashes) and returns the canonical E.164
 * `+91XXXXXXXXXX`, or null when it is not a plausible Indian mobile.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Strip to the 10-digit national mobile number, dropping any +91 / 0091 / 0 prefix. */
function nationalDigits(input: string): string | null {
  let national = input.replace(/\D/g, "");
  if (!national) return null;
  // Drop a leading "00" international prefix, then a "91" country code, then a
  // trunk "0" — in that order — whenever doing so leaves more than 10 digits.
  if (national.length > 10 && national.startsWith("00")) national = national.slice(2);
  if (national.length > 10 && national.startsWith("91")) national = national.slice(2);
  if (national.length > 10 && national.startsWith("0")) national = national.slice(1);
  return national;
}

/** True when `national` is a plausible Indian mobile: 10 digits starting 6–9. */
function isPlausibleIndianMobile(national: string): boolean {
  return /^[6-9]\d{9}$/.test(national);
}

/**
 * Normalize any real-world Indian-mobile input to canonical E.164
 * (`+91XXXXXXXXXX`), or return null when it is not a plausible Indian mobile.
 */
export function normalizeIndianPhone(input: string): string | null {
  const national = nationalDigits(input);
  if (!national || !isPlausibleIndianMobile(national)) return null;
  return `+91${national}`;
}

/** True when `input` normalizes to a valid Indian mobile. */
export function isValidIndianPhone(input: string): boolean {
  return normalizeIndianPhone(input) !== null;
}

/** Keep only the typed national-number digits (max 10) for the controlled input. */
export function formatIndianPhoneInput(input: string): string {
  const national = nationalDigits(input);
  if (national === null) return "";
  return national.slice(0, 10);
}

/**
 * Optional email: empty is allowed (returns true); a non-empty value must be a
 * basic `local@domain.tld` shape.
 */
export function isValidOptionalEmail(input: string): boolean {
  const v = input.trim();
  if (!v) return true;
  return EMAIL_RE.test(v);
}
